/**
 * Reusable Stripe Connect Provider
 * Initializes Stripe Connect instance and provides it to child components
 *
 * Features:
 * - Dynamic theme updates when dark/light mode changes
 * - Skeleton loading state during initialization
 * - Automatic session refresh via fetchClientSecret
 */

import { cn } from "@plasma/utils/cn";
import {
  loadConnectAndInitialize,
  type StripeConnectInstance,
} from "@stripe/connect-js";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import { useAction } from "convex/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { stripeAccountsCreateAccountSession } from "~/api-refs";
import { useTheme } from "~/components/theme-provider";
import { env } from "~/env";
import { logger, PortalEvents } from "~/lib/logger";
import { getStripeAppearance } from "~/lib/stripe-theme";

interface ConnectProviderProps {
  organizationId: string;
  children: ReactNode;
  components?: string[];
  /** Additional class names for the container */
  className?: string;
}

export const ConnectProvider = ({
  organizationId,
  children,
  components,
  className,
}: ConnectProviderProps) => {
  const { resolvedTheme } = useTheme();
  const createAccountSession = useAction(stripeAccountsCreateAccountSession);

  const [stripeConnectInstance, setStripeConnectInstance] =
    useState<StripeConnectInstance | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track if we've initialized to prevent re-initialization on theme change
  const hasInitialized = useRef(false);
  const instanceRef = useRef<StripeConnectInstance | null>(null);

  const isDark = resolvedTheme === "dark";

  // Function to fetch a fresh client secret - called by Stripe when session expires
  // Per Stripe docs: "On long running sessions, the session from the initially provided
  // client secret might expire. When it expires, we automatically use fetchClientSecret
  // to retrieve a new client secret and refresh the session."
  const fetchClientSecret = useCallback(async (): Promise<string> => {
    const { clientSecret } = await createAccountSession({
      organizationId,
      components,
    });
    return clientSecret;
  }, [createAccountSession, organizationId, components]);

  // Initialize Stripe Connect (only once)
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    const initializeConnect = () => {
      try {
        setIsInitializing(true);
        setError(null);

        // Initialize Stripe Connect with current theme
        // (Convex actions invoked via fetchClientSecret carry the vortex-auth
        // bearer token automatically — no manual token plumbing needed.)
        // Pass fetchClientSecret as a function so Stripe can call it to refresh expired sessions
        const publishableKey = env.PUBLIC_STRIPE_PUBLISHABLE_KEY;

        const instance = loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret,
          appearance: getStripeAppearance(isDark),
        });

        instanceRef.current = instance;
        setStripeConnectInstance(instance);
        hasInitialized.current = true;

        logger.info(PortalEvents.STRIPE_CONNECT_INITIALIZED, {
          organization_id: organizationId,
          theme: isDark ? "dark" : "light",
          components: components ?? [],
        });
      } catch (err) {
        const connectError =
          err instanceof Error
            ? err
            : new Error("Failed to initialize Connect");
        setError(connectError.message);

        logger.exception(PortalEvents.STRIPE_CONNECT_ERROR, err, {
          organization_id: organizationId,
          error_message: connectError.message,
        });
      } finally {
        setIsInitializing(false);
      }
    };

    initializeConnect();
  }, [fetchClientSecret, isDark, organizationId, components]);

  // Update appearance when theme changes (after initialization)
  useEffect(() => {
    if (instanceRef.current && hasInitialized.current) {
      // Use the update API to change appearance without re-initializing
      instanceRef.current.update({
        appearance: getStripeAppearance(isDark),
      });

      logger.debug(PortalEvents.STRIPE_CONNECT_THEME_UPDATED, {
        organization_id: organizationId,
        theme: isDark ? "dark" : "light",
      });
    }
  }, [isDark, organizationId]);

  if (isInitializing) {
    return (
      <div className={cn("animate-pulse", className)}>
        {/* Skeleton loader that matches typical Stripe component structure */}
        <div className="space-y-4 p-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between">
            <div className="bg-muted h-6 w-48 rounded-md" />
            <div className="bg-muted h-9 w-24 rounded-md" />
          </div>
          {/* Table header skeleton */}
          <div className="border-border flex gap-4 border-b pb-3">
            <div className="bg-muted h-4 w-24 rounded" />
            <div className="bg-muted h-4 w-32 rounded" />
            <div className="bg-muted h-4 w-20 rounded" />
            <div className="bg-muted h-4 flex-1 rounded" />
          </div>
          {/* Table rows skeleton */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div className="flex gap-4 py-3" key={i}>
              <div className="bg-muted h-4 w-24 rounded" />
              <div className="bg-muted h-4 w-32 rounded" />
              <div className="bg-muted h-4 w-20 rounded" />
              <div className="bg-muted h-4 flex-1 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn("flex h-full items-center justify-center p-6", className)}
      >
        <div className="bg-destructive/10 flex max-w-md flex-col gap-3 rounded-lg p-6 text-center">
          <h2 className="h3 font-heading text-destructive">Failed to load</h2>
          <p className="body text-muted-foreground">{error}</p>
          <p className="body-sm text-muted-foreground">
            Please contact support at{" "}
            <a
              className="subtitle-sm text-foreground"
              href="mailto:hi@vortexnyc.com"
            >
              hi@vortexnyc.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!stripeConnectInstance) {
    return null;
  }

  return (
    <div className={cn("relative", className)}>
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        {children}
      </ConnectComponentsProvider>
    </div>
  );
};
